import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { useSiteChrome } from "@/lib/site";
import { BlogHeader, BlogFooter } from "@/components/blog/BlogChrome";
import { MarkdownBody } from "@/components/blog/MarkdownBody";

const Progress = () => {
  const [p, setP] = useState(0);
  useEffect(() => {
    const on = () => { const h = document.documentElement; setP(Math.min(100, (h.scrollTop / Math.max(1, h.scrollHeight - h.clientHeight)) * 100)); };
    window.addEventListener("scroll", on, { passive: true });
    on();
    return () => window.removeEventListener("scroll", on);
  }, []);
  return <div className="fixed left-0 top-0 z-40 h-[3px] bg-[#e8703a]" style={{ width: `${p}%` }} data-testid="blog-progress" />;
};

const Contents = ({ chapters, current }) => (
  <ol className="space-y-1">
    {chapters.map((c) => (
      <li key={c.slug}>
        <Link to={`/blog/${c.slug}`} data-testid={`blog-toc-${c.slug}`} aria-current={c.slug === current ? "page" : undefined}
          className={`grid grid-cols-[26px_1fr] gap-2 rounded-lg px-2 py-1.5 text-[13.5px] leading-snug transition-colors ${c.slug === current ? "bg-[#17201b] text-white" : "text-[#4c5a53] hover:bg-[#ebe8de]"}`}>
          <span className="font-bold">{String(c.chapter).padStart(2, "0")}</span><span>{c.title}</span>
        </Link>
      </li>
    ))}
  </ol>
);

const Thread = ({ label, p, align, testId }) => p ? (
  <Link to={`/blog/${p.slug}`} data-testid={testId} className={`group block flex-1 rounded-2xl border border-[#d9ddd3] bg-white p-6 transition-shadow hover:shadow-[0_18px_40px_-26px_rgba(23,32,27,.5)] ${align}`}>
    <span className="text-[12px] font-bold tracking-[0.16em] text-[#0f7c78]">{label}</span>
    <span className="mt-2 block font-['Georgia'] text-[21px] leading-snug text-[#17201b] group-hover:text-[#0f7c78]">Chapter {p.chapter}: {p.title}</span>
    {p.summary && <span className="mt-2 line-clamp-2 block text-[14px] text-[#4c5a53]">{p.summary}</span>}
  </Link>
) : <div className="hidden flex-1 md:block" />;

export default function BlogPost() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [missing, setMissing] = useState(false);
  useSiteChrome(p ? `${p.title} | Art of Resume Blog` : "Art of Resume Blog");

  useEffect(() => {
    setP(null); setMissing(false);
    axios.get(`${API}/blog/posts/${slug}`).then((r) => { setP(r.data); window.scrollTo(0, 0); }).catch(() => setMissing(true));
  }, [slug]);

  const chapters = Array.isArray(p?.chapters) ? p.chapters : [];
  return (
    <div className="min-h-screen bg-[#f6f4ee]" data-testid="blog-post-page">
      <Progress />
      <BlogHeader />
      {missing && <main className="mx-auto max-w-[720px] px-5 py-24 text-center"><h1 className="font-['Georgia'] text-4xl text-[#17201b]">This chapter isn't here.</h1><Link to="/blog" className="mt-6 inline-block font-semibold text-[#0f7c78]">← Back to the blog</Link></main>}
      {!p && !missing && <p className="py-24 text-center text-[#4c5a53]" data-testid="blog-post-loading">Turning the page…</p>}
      {p && (
        <main className="mx-auto grid max-w-[1240px] gap-10 px-5 lg:grid-cols-[250px_minmax(0,720px)] lg:justify-center">
          {chapters.length > 0 && <aside className="hidden lg:block"><div className="sticky top-24 max-h-[calc(100vh-120px)] overflow-y-auto pb-6 pt-14">
            <Link to="/blog" className="mb-3 block px-2 text-[12px] font-bold tracking-[0.16em] text-[#0f7c78]">CONTENTS</Link><Contents chapters={chapters} current={p.slug} />
          </div></aside>}
          <article className="min-w-0 pt-12 md:pt-16" data-testid="blog-article">
            {chapters.length > 0 && <details className="mb-8 rounded-xl border border-[#d9ddd3] bg-white p-4 lg:hidden" data-testid="blog-mobile-toc">
              <summary className="cursor-pointer text-[14px] font-semibold text-[#17201b]">All {chapters.length} chapters</summary><div className="mt-3"><Contents chapters={chapters} current={p.slug} /></div>
            </details>}
            <p className="text-[12px] font-bold tracking-[0.18em] text-[#0f7c78]">{p.series ? `${p.series.toUpperCase()} · CHAPTER ${p.chapter}` : "ARTICLE"}</p>
            <h1 data-testid="blog-title" className="mt-4 font-['Georgia'] text-4xl font-normal leading-[1.08] text-[#17201b] sm:text-5xl">{p.title}</h1>
            <p className="mt-5 text-[14px] font-semibold text-[#4c5a53]">{p.reading_minutes} min read · Art of Resume</p>
            {p.previous && <p className="mt-8 rounded-xl bg-[#ebe8de] px-5 py-3 text-[15px] text-[#26302b]" data-testid="blog-previously">
              <b>Previously:</b> <Link to={`/blog/${p.previous.slug}`} className="text-[#0f7c78] underline underline-offset-2">{p.previous.title}</Link>. This chapter picks up the thread from there.</p>}
            <div className="mt-6"><MarkdownBody body={p.body} /></div>
            <nav className="mt-16 flex flex-col gap-4 md:flex-row" data-testid="blog-thread">
              <Thread label="← PREVIOUSLY" p={p.previous} testId="blog-prev-link" />
              <Thread label="NEXT CHAPTER →" p={p.next} align="md:text-right" testId="blog-next-link" />
            </nav>
            {!p.next && p.series && <p className="mt-8 rounded-2xl bg-[#17201b] p-6 text-[16px] leading-relaxed text-white" data-testid="blog-series-end">You've reached the end of <b>{p.series}</b>. Ready to turn understanding into proof? <Link to="/resume-service" className="font-semibold text-[#7fd4c9] underline">Show it on your resume →</Link></p>}
          </article>
        </main>
      )}
      <BlogFooter />
    </div>
  );
}
